<template>
  <div class="verifications-list">
    <div class="verifications-list__filters app-list-filters">
      <input-field
        class="
          app-list-filters__field
          verifications-list__field"
        v-model.trim="filters.id"
        :label="'verifications-list.lbl-id' | globalize"
      />

      <input-field
        class="
          app-list-filters__field
          verifications-list__field"
        v-model.trim="filters.rememberMeId"
        :label="'verifications-list.lbl-remember-me-id' | globalize"
      />

      <select-field
        class="
          app-list-filters__field
          verifications-list__field"
        v-model.trim="filters.status"
        :label="'verifications-list.lbl-verify-status' | globalize"
      >
        <option
          v-for="(item, id) in quickbuyVerificationStatuses"
          :key="`${item}-${id}`"
          :value="item"
        >
          {{ item | formatVerificationStatus }}
        </option>
      </select-field>

      <select-field
        class="
          app-list-filters__field
          verifications-list__field"
        v-model.trim="filters.type"
        :label="'verifications-list.lbl-type' | globalize"
      >
        <option
          v-for="(item, id) in quickbuyVerificationTypes"
          :key="`${item}-${id}`"
          :value="item"
        >
          {{ item | formatVerificationType }}
        </option>
      </select-field>
    </div>

    <div class="verifications-list__wrp app-list">
      <div class="app-list__header">
        <span class="app-list__cell app-list__cell--left">
          {{ "verifications-list.lbl-id" | globalize }}
        </span>
        <span class="app-list__cell app-list__cell--center">
          {{ "verifications-list.lbl-remember-me-id" | globalize }}
        </span>
        <span class="app-list__cell app-list__cell--center">
          {{ "verifications-list.lbl-verify-status" | globalize }}
        </span>
        <span class="app-list__cell app-list__cell--right">
          {{ "verifications-list.lbl-type" | globalize }}
        </span>
      </div>
      <template
        v-if="isLoaded && quickbuyVerificationsList.length && !isFailed">
        <button
          class="app-list__li"
          v-ripple
          v-for="item in quickbuyVerificationsList"
          :key="`verifications-${item.id}`"
          @click="redirectToQuickbuyVerificationDetails(item.id)"
        >
          <span
            class="app-list__cell app-list__cell--left"
            :title="item.id"
          >
            {{ item.id }}
          </span>

          <span
            class="app-list__cell app-list__cell--center"
            :title="item.rememberMeId"
          >
            {{ item.rememberMeId }}
          </span>

          <span
            class="app-list__cell app-list__cell--center"
            :title="item.status"
          >
            {{ item.status | formatVerificationStatus }}
          </span>

          <span
            class="app-list__cell app-list__cell--right"
            :title="item.type"
          >
            {{ item.type | formatVerificationType }}
          </span>
        </button>
      </template>

      <template v-else>
        <div class="app-list__li-like">
          <template v-if="isLoaded && !quickbuyVerificationsList.length">
            <p>
              {{ "verifications-list.nothing-here-yet" | globalize }}
            </p>
          </template>

          <template v-else-if="isFailed">
            <p>
              {{ "verifications-list.default-error-message" | globalize }}
            </p>
          </template>

          <template v-else>
            <p>
              {{ "verifications-list.loading-message" | globalize }}
            </p>
          </template>
        </div>
      </template>
    </div>

    <collection-loader
      class="app__more-btn-wrp"
      :first-page-loader="getQuickbuyVerifications"
      @first-page-load="setList"
      @next-page-load="concatList"
      ref="collectionLoaderBtn"
    />
  </div>
</template>

<script>
import CollectionLoader from '@/vue/common/CollectionLoader'
import SelectField from '@/vue/fields/SelectField'
import InputField from '@/vue/fields/InputField'

import { ErrorHandler } from '@/js/helpers/error-handler'
import { clearObject } from '@/js/helpers/clear-object'
import { api } from '@/api'
import { vueRoutes } from '@/vue-router/routes'
import { QuickbuyVerificationRecord } from '@/js/records/entities/quickbuy-verification.record'
import { addParamsToLocation, queryToFilters } from '@/js/helpers/router-query'

const defaultFilters = {
  id: '',
  rememberMeId: '',
  type: '',
  subtype: '',
  status: '',
}

export default {
  name: 'quickbuy-verifications-list',
  components: {
    CollectionLoader,
    SelectField,
    InputField,
  },
  data () {
    return {
      isLoaded: false,
      isFailed: false,
      isPending: false,
      quickbuyVerificationsList: [],
      quickbuyVerificationStatuses: ['pending', 'approved', 'rejected'],
      quickbuyVerificationTypes: ['quickbuy_userdata', 'quickbuy_redeem'],
      filters: {
        id: '',
        rememberMeId: '',
        type: '',
        subtype: '',
        status: '',
      },
    }
  },

  watch: {
    filters: {
      handler () {
        this.isPending = true
        if (this.isLoaded) {
          this.reloadList()
        }
      },
      deep: true,
    },
    isLoaded: {
      handler (value) {
        if (value && this.isPending) {
          this.reloadList()
          this.isPending = false
        }
      },
    },
    '$route' () {
      const filters = queryToFilters(defaultFilters)
      this.filters = filters
      return this.getQuickbuyVerifications()
    },
  },

  async created () {
    this.filters = queryToFilters(defaultFilters)
    await this.getQuickbuyVerifications()
  },
  methods: {
    async getQuickbuyVerifications () {
      this.isLoaded = false
      this.isFailed = false
      let response = {}
      try {
        const query = {
          filter: clearObject({
            id: this.filters.id,
            remember_me_id: this.filters.rememberMeId,
            type: this.filters.type,
            status: this.filters.status,
          }),
        }

        response = await api.getWithSignature('/quickbuy/verifications', query)
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
        this.isFailed = true
      }

      this.isLoaded = true
      return response
    },

    setList (data) {
      this.quickbuyVerificationsList = data.map(
        i => new QuickbuyVerificationRecord(i)
      )
    },

    concatList (data) {
      this.quickbuyVerificationsList = this.quickbuyVerificationsList.concat(
        data.map(i => new QuickbuyVerificationRecord(i)))
    },

    reloadList () {
      addParamsToLocation(this.$route, this.filters)
      this.$refs.collectionLoaderBtn.loadFirstPage()
    },

    redirectToQuickbuyVerificationDetails (id) {
      this.$router.push({
        name: vueRoutes.quickbuyVerificationDetails.name,
        params: { id },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.verifications-list__wrp {
  padding-top: 4rem;
}

.verifications-list__field {
  min-width: 16.5rem;
}
</style>
